import React, { useState, useEffect } from "react";
import {
  ContainerSelected,
  Registry,
} from "components/Environment/shared/BuildTypes";
import { FlatCard } from "components/UI-lib";
import { Button } from "components/Graphs/Button";
import { RadioGroup, RadioGroupItem } from "components/Graphs/RadioGroup";
import { Input } from "components/Graphs/Input";
import { TrashIcon } from "@heroicons/react/24/solid";
import agent, { ValidateDockerComposeReqBody } from "server";
import { replaceBlobWithRaw } from "components/utils";
import yaml from "js-yaml";
import { XCircleIcon } from "@heroicons/react/24/outline";

export interface DockerComposeSelectorProps {
  containerSelected?: ContainerSelected;
  onSelectContainer: (container: ContainerSelected) => void;
  hideCredentials: boolean;
  hideSaveAndContinue?: boolean;
  onReset?: () => void;
  jupyterInstall?: boolean;
  onJupyterInstallChange?: (install: boolean) => void;
  onDraftChange?: (container: ContainerSelected) => void;
  onComposeValidationChange?: (isValid: boolean) => void;
  presentation?: "card" | "sections";
}

interface Port {
  mode: string;
  target: number;
  published: string;
  protocol: string;
}

interface Service {
  name: string;
  image: string;
  ports: Port[];
  restart: string;
  scale: number;
}

interface Project {
  services: Service[];
}

interface ComposeConfig {
  project: Project;
}

interface RegistryWithValidation extends Registry {
  errors?: {
    url?: string;
    username?: string;
    password?: string;
  };
}

const DockerCompose: React.FC<DockerComposeSelectorProps> = ({
  containerSelected,
  onSelectContainer,
  hideCredentials,
  hideSaveAndContinue,
  onReset,
  jupyterInstall = false,
  onJupyterInstallChange,
  onDraftChange,
  onComposeValidationChange,
  presentation = "card",
}) => {
  const localFileRadioId = React.useId();
  const repositoryUrlRadioId = React.useId();
  const [uploadMethod, setUploadMethod] = useState<"file" | "github">(
    containerSelected?.dockerCompose?.fileUrl ? "github" : "file",
  );
  const [gitUrl, setGitUrl] = useState(
    containerSelected?.dockerCompose?.fileUrl || "",
  );
  const [yamlString, setYamlString] = useState<string>(
    containerSelected?.dockerCompose?.yamlString || "",
  );
  const [file, setFile] = useState<File | null>(null);

  const [loadingValidation, setLoadingValidation] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>("");
  const [validationResponse, setValidationResponse] =
    useState<ComposeConfig | null>(null);
  const [validatedContainer, setValidatedContainer] =
    useState<ContainerSelected | null>(null);

  const [loadingFile, setLoadingFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [registryError, setRegistryError] = useState<string | null>(null);

  const [registries, setRegistries] = useState<Array<RegistryWithValidation>>(
    containerSelected?.dockerCompose?.registries?.map((r) => ({
      ...r,
      errors: {},
    })) || [],
  );
  const registriesRef = React.useRef(registries);

  // Add ref for the registry container
  const registryContainerRef = React.useRef<HTMLDivElement>(null);

  // Add useEffect to validate pre-selected docker compose
  useEffect(() => {
    if (containerSelected?.dockerCompose) {
      if (containerSelected.dockerCompose.fileUrl) {
        validateDockerCompose(
          "github",
          containerSelected.dockerCompose.fileUrl,
          true,
        );
      } else if (containerSelected.dockerCompose.yamlString) {
        validateDockerCompose(
          "file",
          containerSelected.dockerCompose.yamlString,
          true,
        );
      }
    }
  }, [containerSelected, hideSaveAndContinue]);

  const validateDockerCompose = async (
    method: "file" | "github",
    data: string,
    isAutoValidation = false,
  ) => {
    if (hideSaveAndContinue && validationResponse) {
      return;
    }

    setLoadingValidation(true);
    setValidationError("");
    setValidationResponse(null);
    setValidatedContainer(null);
    onComposeValidationChange?.(false);
    const body: ValidateDockerComposeReqBody = {
      fileUrl: method === "github" ? replaceBlobWithRaw(data) : undefined,
      yamlString: method === "file" ? data : undefined,
    };
    try {
      const dockerCompose = await agent.Workspaces.validateDockerCompose(body);
      if (!dockerCompose.project) {
        throw new Error("Docker Compose validation did not return a project");
      }
      const composeConfig = { project: dockerCompose.project };
      setValidationResponse(composeConfig);
      onComposeValidationChange?.(true);

      let parsedYaml: string | undefined = method === "file" ? data : undefined;
      if (method === "github") {
        const services = composeConfig.project.services;
        if (services?.length) {
          const servicesObj: Record<string, any> = {};
          for (const svc of services) {
            servicesObj[svc.name] = {
              image: svc.image,
              ports: (svc.ports || []).map((p: any) => ({
                published: p.published,
                target: p.target,
              })),
            };
          }
          parsedYaml = yaml.dump({ version: "3", services: servicesObj });
        }
      }

      const validatedContainer: ContainerSelected = {
        dockerCompose: {
          fileUrl: method === "github" ? replaceBlobWithRaw(data) : undefined,
          yamlString: method === "file" ? data : undefined,
          parsedYaml,
          jupyterInstall,
          registries: registriesRef.current,
        },
      };
      setValidatedContainer(validatedContainer);

      if (!isAutoValidation) {
        onDraftChange?.(validatedContainer);
      }
      if (hideSaveAndContinue && !isAutoValidation) {
        onSelectContainer(validatedContainer);
      }
    } catch (error) {
      setValidatedContainer(null);
      onComposeValidationChange?.(false);
      setValidationError(
        error instanceof Error
          ? error.message
          : "Failed to validate Docker Compose",
      );
    }
    setLoadingValidation(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setLoadingFile(true);
      setFileError(null);
      setValidationError("");
      setValidationResponse(null);
      setValidatedContainer(null);
      onComposeValidationChange?.(false);
      setYamlString("");
      setFile(selectedFile);
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          if (!text.trim()) {
            throw new Error("File is empty");
          }
          setYamlString(text);
          setLoadingFile(false);
          void validateDockerCompose("file", text);
        } catch (error) {
          setFileError(
            error instanceof Error ? error.message : "Failed to read file",
          );
          setLoadingFile(false);
        }
      };

      reader.onerror = () => {
        setFileError("Failed to read file");
        setLoadingFile(false);
      };

      reader.readAsText(selectedFile);
    }
  };

  const validateAllRegistries = (): boolean => {
    let areAllRegistriesValid = true;
    const updatedRegistries = registries.map((registry) => {
      const errors: RegistryWithValidation["errors"] = {};

      if (!registry.url.trim()) {
        errors.url = "Registry URL is required";
      } else if (!registry.url.includes(".")) {
        errors.url = "Please enter a valid URL";
      }
      if (!registry.username.trim()) {
        errors.username = "Username is required";
      }
      if (!registry.password.trim()) {
        errors.password = "Password is required";
      }

      if (Object.keys(errors).length > 0) {
        areAllRegistriesValid = false;
      }
      return { ...registry, errors };
    });

    setRegistries(updatedRegistries);
    return areAllRegistriesValid;
  };

  const updateRegistriesAndDraft = (
    updatedRegistries: RegistryWithValidation[],
  ) => {
    registriesRef.current = updatedRegistries;
    setRegistries(updatedRegistries);
    setRegistryError(null);

    if (validatedContainer?.dockerCompose) {
      const updatedContainer: ContainerSelected = {
        dockerCompose: {
          ...validatedContainer.dockerCompose,
          registries: updatedRegistries,
        },
      };
      setValidatedContainer(updatedContainer);
      onDraftChange?.(updatedContainer);
    }
  };

  const addRegistry = () => {
    updateRegistriesAndDraft([
      ...registries,
      { url: "", username: "", password: "", errors: {} },
    ]);
    setTimeout(() => {
      registryContainerRef.current?.scrollTo({
        top: registryContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 0);
  };

  const removeRegistry = (index: number) => {
    updateRegistriesAndDraft(registries.filter((_, i) => i !== index));
  };

  const updateRegistry = (
    index: number,
    field: "url" | "username" | "password",
    value: string,
  ) => {
    const updatedRegistries = [...registries];
    updatedRegistries[index] = {
      ...updatedRegistries[index],
      [field]: value,
      errors: {
        ...updatedRegistries[index].errors,
        [field]: undefined, // Clear the error when the field is updated
      },
    };
    updateRegistriesAndDraft(updatedRegistries);
  };

  const resetDockerCompose = () => {
    setValidationResponse(null);
    setValidatedContainer(null);
    onComposeValidationChange?.(false);
    setUploadMethod("file");
    setGitUrl("");
    setYamlString("");
    setFile(null);
  };

  const registryCredentialsSection = !hideCredentials ? (
    <div
      className={
        presentation === "sections" ? "mt-6 space-y-4" : "mt-4 space-y-4"
      }
    >
      <div
        className={
          presentation === "sections"
            ? "text-base font-medium text-white"
            : "text-sm font-semibold"
        }
      >
        {presentation === "sections"
          ? "Docker registry credentials"
          : "Docker Registry Credentials"}
      </div>
      {registries.length > 0 && (
        <div ref={registryContainerRef} className="max-h-[200px] overflow-auto">
          {registries.map((reg, index) => (
            <div
              key={index}
              className="flex flex-col space-y-2 py-2 border-b border-zinc-800 last:border-b-0"
            >
              <div className="flex space-x-2">
                <div className="flex flex-col flex-1">
                  <Input
                    placeholder="Registry URL"
                    value={reg.url}
                    onChange={(e) =>
                      updateRegistry(index, "url", e.target.value)
                    }
                    className={reg.errors?.url ? "border-red-500" : ""}
                  />
                  {reg.errors?.url && (
                    <span className="text-red-500 text-xs mt-1">
                      {reg.errors.url}
                    </span>
                  )}
                </div>
                <div className="flex flex-col flex-1">
                  <Input
                    placeholder="Username"
                    value={reg.username}
                    onChange={(e) =>
                      updateRegistry(index, "username", e.target.value)
                    }
                    className={reg.errors?.username ? "border-red-500" : ""}
                  />
                  {reg.errors?.username && (
                    <span className="text-red-500 text-xs mt-1">
                      {reg.errors.username}
                    </span>
                  )}
                </div>
                <div className="flex flex-col flex-1">
                  <Input
                    type="password"
                    placeholder="Password"
                    value={reg.password}
                    onChange={(e) =>
                      updateRegistry(index, "password", e.target.value)
                    }
                    className={reg.errors?.password ? "border-red-500" : ""}
                  />
                  {reg.errors?.password && (
                    <span className="text-red-500 text-xs mt-1">
                      {reg.errors.password}
                    </span>
                  )}
                </div>
                <div className="flex flex-col">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => removeRegistry(index)}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {registryError && (
        <div className="text-sm text-red-500">{registryError}</div>
      )}
      <Button
        type="button"
        variant="noshow"
        onClick={addRegistry}
        className="w-fit text-sm font-medium text-highlight hover:text-highlightDarker hover:underline"
      >
        + Add registry credentials
      </Button>
    </div>
  ) : null;

  return (
    <>
      <div className="flex flex-col min-h-full">
        <div className="flex flex-row text-white w-full">
          <div className="flex flex-col w-full gap-4">
            <div>
              <FlatCard
                noBorder={presentation === "sections"}
                className={
                  presentation === "sections"
                    ? "border-0 !bg-transparent !p-0 shadow-none"
                    : ""
                }
              >
                <div>
                  <div className="flex flex-col mb-3">
                    <div
                      className={
                        presentation === "sections"
                          ? "text-base font-medium text-white"
                          : "text-lg font-semibold"
                      }
                    >
                      {presentation === "sections"
                        ? "Docker compose file"
                        : "Upload Docker Compose"}
                    </div>
                    <div className="mt-1 text-sm text-secondary">
                      Choose to upload a local file or provide a GitHub/Gitlab
                      URL
                    </div>
                  </div>
                  {!validationResponse ? (
                    <div className="flex flex-col mb-5">
                      <RadioGroup
                        value={uploadMethod}
                        onValueChange={(value) =>
                          setUploadMethod(value as "file" | "github")
                        }
                      >
                        <div className="flex items-center gap-x-3">
                          <RadioGroupItem value="file" id={localFileRadioId} />
                          <label
                            htmlFor={localFileRadioId}
                            className="cursor-pointer text-sm"
                          >
                            Upload Local File
                          </label>
                        </div>
                        <div className="flex items-center gap-x-3">
                          <RadioGroupItem
                            value="github"
                            id={repositoryUrlRadioId}
                          />
                          <label
                            htmlFor={repositoryUrlRadioId}
                            className="cursor-pointer text-sm"
                          >
                            Provide GitHub/Gitlab URL
                          </label>
                        </div>
                      </RadioGroup>
                      <div className="mt-4">
                        {uploadMethod === "file" ? (
                          <div className="space-y-2">
                            <div className="text-sm font-semibold mb-2">
                              Docker compose file
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                type="file"
                                accept=".yml,.yaml"
                                onChange={handleFileChange}
                                disabled={loadingFile || loadingValidation}
                                className="min-w-0 flex-1 text-xs lg:text-sm"
                              />
                            </div>
                            {(loadingFile || loadingValidation) && (
                              <div className="text-sm text-secondary">
                                Validating Docker Compose file...
                              </div>
                            )}
                            {fileError && (
                              <div className="text-red-500">{fileError}</div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="text-sm font-semibold mb-2">
                              GitHub/Gitlab URL
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                type="url"
                                placeholder="https://github.com/user/repo/blob/main/docker-compose.yml"
                                value={gitUrl}
                                onChange={(e) => setGitUrl(e.target.value)}
                                className="min-w-0 flex-1 text-xs lg:text-sm"
                              />
                              <Button
                                type="button"
                                className="shrink-0 disabled:text-zinc-500 disabled:opacity-70 dark:disabled:text-zinc-200"
                                onClick={() =>
                                  validateDockerCompose("github", gitUrl)
                                }
                                disabled={loadingValidation || gitUrl === ""}
                              >
                                {loadingValidation ? "Submitting..." : "Submit"}
                              </Button>
                            </div>
                          </div>
                        )}
                        {validationError && (
                          <div className="mt-2 text-sm text-red-500">
                            Error: {validationError}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div className="text-sm truncate">
                        <span className="font-bold">
                          {uploadMethod === "file"
                            ? "Uploaded File: "
                            : "URL: "}
                        </span>
                        <span className="truncate">
                          {uploadMethod === "file"
                            ? yamlString
                              ? file?.name || "From Local Computer"
                              : "None"
                            : gitUrl || "None"}
                        </span>
                      </div>
                      <div className="flex flex-row items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            resetDockerCompose();
                            onReset?.();
                          }}
                          className="text-red-500"
                        >
                          <XCircleIcon className="w-4 h-4 mr-1 text-red-500" />
                          <span className="text-red-500">Reset</span>
                        </Button>
                      </div>
                    </div>
                  )}
                  {validationResponse && (
                    <div>
                      <div className="mb-3 text-sm font-semibold text-white">
                        Services Preview
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        {validationResponse.project.services?.map((service) => (
                          <FlatCard
                            className="bg-secondaryBg p-4 w-full flex flex-row rounded-md border-2 border-gray-300 dark:border-zinc-800 h-full"
                            key={service.name}
                          >
                            <div className="flex flex-col h-full text-sm">
                              <div className="flex flex-col">
                                <div className="font-medium">{service.name}</div>
                                <div className="flex flex-row">
                                  <span className="font-bold mr-1">Image:</span>
                                  <span
                                    className="truncate"
                                    title={service.image}
                                  >
                                    {service.image}
                                  </span>
                                </div>
                              </div>
                              {service.ports && service.ports.length > 0 && (
                                <div>
                                  <span className="font-bold mr-1">Ports:</span>
                                  <span>
                                    {service.ports
                                      .map((port) => port.published)
                                      .join(", ")}
                                  </span>
                                </div>
                              )}
                            </div>
                          </FlatCard>
                        ))}
                      </div>
                    </div>
                  )}
                  {registryCredentialsSection}
                </div>
              </FlatCard>
            </div>
            {validationResponse && !hideSaveAndContinue && (
              <Button
                className="w-full"
                onClick={() => {
                  if (!validateAllRegistries()) {
                    setRegistryError(
                      "Please fill out all registry fields correctly",
                    );
                    return;
                  }
                  if (validatedContainer?.dockerCompose) {
                    onSelectContainer({
                      dockerCompose: {
                        ...validatedContainer.dockerCompose,
                        registries,
                      },
                    });
                  }
                }}
              >
                Save and Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DockerCompose;
